/* eslint-disable react-hooks/exhaustive-deps */
import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { FC, ReactNode, useEffect, useState, type JSX } from 'react';
import validator from 'validator';
import { Nullable } from '../../types';
import { ExtendUserBasicType, UserBasicItem } from './user-basic-item';
import { UserBasicTitle } from './user-basic-title';

interface Props {
  anotherUserLabel: string;
  disabled: boolean;
  fields: ExtendUserBasicType[];
  hint: string;
  onValidation?: (value: boolean) => void;
  setFields: (users: ExtendUserBasicType[]) => void;
  showDisplay?: boolean;
  showRemove?: boolean;
  showSms?: boolean;
  title: ReactNode;
}

const StyledAccordion = styled(Accordion)({
  boxShadow: 'none',
});

const StyledAccordionSummary = styled(AccordionSummary)({
  '&.Mui-expanded': { minHeight: '3rem', overflow: 'hidden' },
  padding: 0,
});

export const MultipleUserBasicEditor: FC<Props> = ({
  anotherUserLabel,
  disabled,
  fields,
  hint,
  onValidation,
  setFields,
  showDisplay,
  showRemove,
  showSms,
  title,
}) => {
  const [expanded, setExpanded] = useState<string | false>('panel0');

  const handlePanelChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleChange = (value: string, index: number, key: keyof ExtendUserBasicType): Promise<void> => {
    const newBasic = [...fields];
    const updatedUser = { ...newBasic[index] };

    if (key === 'sms' && !value.trim()) {
      delete updatedUser.sms;
    }
    else {
      updatedUser[key] = value;
    }

    newBasic[index] = updatedUser;
    setFields(newBasic);
    return Promise.resolve();
  };

  const handleAdd = (): void => {
    setFields([
      ...fields,
      {
        display: '',
        email: '',
        notificationEmail: '',
        notificationPhone: '',
      },
    ]);
  };

  const handleRemove = (index: number): Promise<void> => {
    const newBasic = [...fields];
    newBasic.splice(index, 1);
    setFields(newBasic);
    return Promise.resolve();
  };

  const renderAnotherUserButton = (): Nullable<JSX.Element> => {
    if (fields.length > 10) return null;

    return (
      <Stack alignItems="flex-end">
        <Button onClick={handleAdd}>
          {anotherUserLabel}
        </Button>
      </Stack>
    );
  };

  useEffect(() => {
    const emailValidCheck = fields.every((value) => value.email.trim() !== '' && validator.isEmail(value.email));
    const displayValidCheck = !showDisplay || fields.every((field) => field.display.trim() !== '');
    onValidation?.(emailValidCheck && displayValidCheck);
  }, [fields, showDisplay, onValidation]);

  return (
    <Stack spacing={2}>
      {title}
      <Typography>{hint}</Typography>
      {fields.map((userItem, index) => {
        return (
          <StyledAccordion
            key={index}
            expanded={expanded === `panel${index}`}
            onChange={handlePanelChange(`panel${index}`)}
          >
            <StyledAccordionSummary
              id={`panel${index}-header`}
              aria-controls={`panel${index}-content`}
            >
              <UserBasicTitle user={userItem} />
            </StyledAccordionSummary>
            <AccordionDetails>
              <UserBasicItem
                disabled={disabled}
                handleChange={handleChange}
                handleRemove={handleRemove}
                index={index}
                showDisplay={showDisplay}
                showRemove={showRemove ?? fields.length > 1}
                showSms={showSms}
                skipValidateEmailEmpty
                userItem={userItem}
              />
            </AccordionDetails>
          </StyledAccordion>
        );
      })}
      <Divider />
      {renderAnotherUserButton()}
    </Stack>
  );
};
